import React from 'react';

import { FormattedMessage } from 'react-intl-next';

import Button from '@atlaskit/button/standard-button';
import PeopleGroupIcon from '@atlaskit/icon/glyph/people-group';
import Tooltip from '@atlaskit/tooltip';
import {
  UnmatchedCommitAuthorAvatar,
  UserAvatar,
} from '@atlassian/bitbucket-user-profile';

import { Row } from 'src/components/details-card';
import { Expander } from 'src/components/sidebar';
import { Commit as CommitType, User as UserType } from 'src/components/types';
import { useIntl } from 'src/hooks/intl';

import messages from './commit-page.i18n';
import { ParticipantRow } from './commit-page.styled';

const ParticipantsIcon = (): JSX.Element => {
  const intl = useIntl();

  return (
    <PeopleGroupIcon label={intl.formatMessage(messages.participantsLabel)} />
  );
};

export const ExpandCommitParticipants = ({
  commit,
}: {
  commit: CommitType;
}): JSX.Element => {
  let users: UserType[] = [];
  let participants: JSX.Element[] = [];
  if (commit.participants && commit.participants.length > 0) {
    users = commit.participants.map(participant => participant.user);
    participants = commit.participants.map(participant => (
      <ParticipantRow key={participant.user.uuid}>
        <UserAvatar
          user={participant.user}
          status={participant.approved ? 'approved' : null}
        />
        {participant.user.display_name}
      </ParticipantRow>
    ));
  }

  // check if the author isn't already in participants
  if (
    commit.author.user &&
    !users.some(user => {
      return user.uuid === commit.author.user?.uuid;
    })
  ) {
    users = [commit.author.user].concat(users);
    participants = [
      <ParticipantRow key={commit.author.user.uuid}>
        <UserAvatar user={commit.author.user} />
        {commit.author.user.display_name}
      </ParticipantRow>,
    ].concat(participants);
  }

  // handle anonymous author
  if (!commit.author.user) {
    participants = [
      <ParticipantRow key={commit.author.raw}>
        <UnmatchedCommitAuthorAvatar rawCommitAuthor={commit.author.raw} />
        {commit.author.raw}
      </ParticipantRow>,
    ].concat(participants);
  }

  let message;
  if (participants.length === 1) message = messages.oneParticipant;
  else message = messages.participants;
  const label = (
    <FormattedMessage
      {...message}
      values={{ count: <strong>{participants.length}</strong> }}
    />
  );

  return (
    <Expander icon={<ParticipantsIcon />} label={label}>
      {participants.map(participant => (
        <Row key={participant.key}>{participant}</Row>
      ))}
    </Expander>
  );
};

export const CollapseCommitParticipants = ({
  commit,
}: {
  commit: CommitType;
}): JSX.Element => {
  const participants = commit.participants || [];

  let participantCount = participants.length;

  if (
    (commit.author.user &&
      !participants.some(participant => {
        return participant.user.uuid === commit.author?.user?.uuid;
      })) ||
    !commit.author.user
  ) {
    participantCount++;
  }

  let message;

  if (participantCount === 1) message = messages.oneParticipant;
  else message = messages.participants;
  const label = (
    <FormattedMessage {...message} values={{ count: participantCount }} />
  );
  return (
    <Tooltip content={label} position="left">
      <Button appearance="subtle" iconBefore={<ParticipantsIcon />} />
    </Tooltip>
  );
};
